/**
 * The search over the stored explorations, on a sheet (D102).
 *
 * The sidebar carried a search box between its doors and its list, and the box
 * filtered the list in place. The owner asked for the reference's shape
 * instead: a magnifying glass beside the collapse button at the head of the
 * pane, opening a search of its own. So the search is a SHEET now — the same
 * shell as every other sheet: the scrim, `useModalFocus`, and a rung on the
 * Escape ladder in `App.tsx`, which also names it in every "is a sheet open"
 * guard, so the `?` key cannot open the keyboard reference over it and clear
 * `inert` under it. `ExplorationSearch.test.tsx` holds that parity.
 *
 * IT LISTS RATHER THAN FILTERS. With nothing typed it shows every stored
 * exploration, newest first — from the rail it is also the quickest way to the
 * whole list — and as somebody types it narrows to the seeds and the project
 * labels that match, ranked the way `searchRecent` ranks them: a seed match
 * before a project match, an earlier match before a later one. The pane
 * behind it keeps every row exactly where it was, which is what a box that
 * filtered the pane could not promise: the groups moved under the pointer,
 * PINNED thinned, and a folded project had to be unfolded behind its owner's
 * back so a match was not hidden under a shut folder (D101).
 *
 * A row prints the exploration's name — its seed clamped like a project label,
 * by the sidebar's own rule (`core/name.ts`, D105), with the whole seed on the
 * row's title — and where it is filed, and not the node count or the age, for the
 * reason the sidebar's rows stopped printing them (D102). ↑ and
 * ↓ move the highlight, Enter opens it, a press on a row opens that row.
 * Escape belongs to the box while it has text — one press empties it and the
 * press stops there — and with the box empty the ladder takes the press and
 * shuts the sheet: D26's two-press contract, on a new surface.
 *
 * Opening a row goes through the door the sidebar's rows and the library's go
 * through, behind the same two locks — a run that is going and a pass in
 * flight both refuse, in their own words — because switching the canvas under
 * either is the one thing this app does not do by accident.
 */

import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { lockHeld, useStore } from '../core/store'
import { listRecent, searchRecent, type RecentEntry } from '../core/persist'
import { projectLabel } from '../core/types'
import { forkLine, shownName } from '../core/name'
import { NO_LIVE_MARKS, rowMark, type LiveMarks, type OpenedMap } from '../core/unread'
import { STATUS_WORD } from '../core/vocabulary'
import { openSession } from '../core/autosave'
import { useRowMarks } from './rowMarks'
import { Icon } from './components/Icon'
import { useModalFocus } from './components/useModalFocus'

/**
 * The sheet itself, pure: everything it prints arrives as props, so a test can
 * render it without a store or a document behind it. The connected shell below
 * is what the app mounts.
 */
export function ExplorationSearchBody({
  q,
  rows,
  active,
  openId,
  onChange,
  onActive,
  onPick,
  onClose,
  openedAt,
  liveMarks = NO_LIVE_MARKS,
  inputRef,
  listRef,
  dialogRef,
}: {
  q: string
  rows: RecentEntry[]
  /** The row under the highlight — the one Enter opens. */
  active: number
  openId: string
  /** When each exploration was last opened here — what puts a mark out
   *  (D103, D334). */
  openedAt: OpenedMap
  /** What is arriving where, and where an answer landed unseen — the live half
   *  of a row's mark (`ui/rowMarks.ts`). Nothing live by default, so the body
   *  renders without a store behind it. */
  liveMarks?: LiveMarks
  onChange: (q: string) => void
  onActive: (i: number) => void
  onPick: (i: number) => void
  onClose: () => void
  inputRef?: RefObject<HTMLInputElement | null>
  listRef?: RefObject<HTMLDivElement | null>
  dialogRef?: RefObject<HTMLDivElement | null>
}) {
  const typed = q.trim()
  return (
    <div
      className="scrim"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="sheet sheet--tall panel"
        role="dialog"
        aria-modal="true"
        aria-label="Search explorations"
        ref={dialogRef}
        tabIndex={-1}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name="search" size={18} strokeWidth={1.5} style={{ color: 'var(--accent)' }} />
          <h2>Search explorations</h2>
        </div>
        <input
          ref={inputRef}
          className="input explsearch"
          type="search"
          value={q}
          // Named, not merely prompted — the palette's reason: a placeholder
          // is gone the moment the field has text, which is exactly when
          // somebody wants to hear what it is searching.
          aria-label="Search explorations"
          placeholder="By what an exploration is about, or by its project"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              // Escape belongs to the box while it has text: one press empties
              // it and stops here, so the ladder does not also shut the sheet
              // on the same press. Empty, the press goes on to the ladder,
              // which shuts the sheet like any other.
              if (q === '') return
              e.stopPropagation()
              onChange('')
            } else if (e.key === 'ArrowDown') {
              e.preventDefault()
              onActive(Math.min(active + 1, rows.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              onActive(Math.max(active - 1, 0))
            } else if (e.key === 'Enter') {
              e.preventDefault()
              onPick(active)
            }
          }}
        />
        <div className="sheet-body scroll explsearch__list" ref={listRef}>
          {rows.length === 0 && (
            <p className="empty-note">
              {typed ? `Nothing here matches “${typed}”.` : 'Nothing stored on this device yet.'}
            </p>
          )}
          {rows.map((e, i) => {
            const name = shownName(e) || 'Untitled exploration'
            const isOpen = e.id === openId
            // The same second line the sidebar's rows print, for the same
            // reason: only what nothing else says — the project, and the demo
            // — and nothing at all when there is none of that. The open one is
            // said by `aria-current` (D103). A fork says where it came from, as
            // its sidebar row does.
            const marks = [
              projectLabel(e.project),
              e.forkedFrom ? forkLine(e.forkedFrom) : null,
              e.demo ? 'demo' : null,
            ].filter((m): m is string => !!m)
            const mark = rowMark(e, openedAt, liveMarks)
            return (
              <button
                key={e.id}
                className="palette-item"
                data-active={i === active}
                aria-current={isOpen || undefined}
                // The whole seed, where the name is a clamp of it.
                title={name !== e.seed ? e.seed : undefined}
                onPointerEnter={() => onActive(i)}
                onClick={() => onPick(i)}
              >
                {/* The sidebar's mark, by the sidebar's one rule (D334). */}
                <span aria-hidden className="explrow__dot" data-mark={mark} />
                <span className="palette-item-main">
                  <b>{name}</b>
                  {marks.length > 0 && <span>{marks.join(' · ')}</span>}
                </span>
                {/* …and said, as the sidebar's row says it. Beside the main
                    column, not in it: `.palette-item-main span` would style it. */}
                {mark && (
                  <span className="sr-only">
                    , {mark === 'running' ? STATUS_WORD.running : STATUS_WORD.unread}
                  </span>
                )}
                {i === active && <span className="kbd">↵</span>}
              </button>
            )
          })}
        </div>
        <div className="sheet-actions">
          <button className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Open one stored exploration from a list of them — this sheet's rows and the
 * palette's (D335), through one guard, so a door that lists explorations cannot
 * be the one that skips it. The palette's rows used to call `openSession` bare,
 * and so could switch the canvas under a live run that every other door refused.
 *
 * Stopping the worked example's unfurl is for leaving it. The open exploration's
 * own row goes nowhere, so it only asks whether a pass that will not yield holds
 * the lock — and the run is asked first either way. Read off the store at the
 * press, not a render. `close` shuts the door's own surface once the open is
 * going through, before the canvas swaps under it. Whether it went through.
 */
export function openFromIndex(id: string, close?: () => void): boolean {
  const st = useStore.getState()
  const runLive = st.run !== null && st.run.phase !== 'ended'
  if (runLive || (id === st.session.id ? lockHeld(st) : !st.yieldUnpaid())) {
    st.toast(
      runLive
        ? 'A run is going. Stop it before opening another exploration — it would stop itself anyway.'
        : 'One thought at a time — let the current branch finish.',
      'warn',
    )
    return false
  }
  // An opening, the open exploration's own row included — the sidebar's rule,
  // so a filled dot goes out from here the way it does from there.
  st.markOpened(id)
  close?.()
  if (id !== st.session.id) openSession(id)
  return true
}

export function ExplorationSearch() {
  const open = useStore((s) => s.searchOpen)
  const setOpen = useStore((s) => s.setSearchOpen)
  const sessionId = useStore((s) => s.session.id)
  /** The OPEN exploration's project, off the live session and not off its
   *  index row — the sidebar's reason: the autosave trails the store by up to
   *  a second, so a row just filed would say its old project here. */
  const openProject = useStore((s) => s.session.project)
  /** The OPEN exploration's latest run's end, read the same way and for the
   *  sidebar's reason: the store has it the moment a run ends and the index a
   *  beat later, and the ring should fill rather than go out first (D334). */
  const openFinishedRunAt = useStore((s) => s.session.runs.at(-1)?.finishedAt)
  const savedAt = useStore((s) => s.savedAt)
  const openedAt = useStore((s) => s.openedAt)
  const liveMarks = useRowMarks()
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  useModalFocus(dialogRef, open)

  // A fresh box on every opening, and the caret in it. `useModalFocus` puts
  // focus on the dialog itself so a reader hears its name first; the field
  // asks one frame later, the way the palette's does.
  useEffect(() => {
    if (!open) return
    setQ('')
    setActive(0)
    const frame = requestAnimationFrame(() => inputRef.current?.focus())
    return () => cancelAnimationFrame(frame)
  }, [open])

  /** Read when the sheet opens and at the autosave's signal, never per node —
   *  the sidebar's own rule for the same index. Nothing while shut: a sheet
   *  that is not on screen must not read the index on every save. */
  const recents = useMemo(
    () =>
      open
        ? listRecent().map((e) =>
            e.id === sessionId
              ? {
                  ...e,
                  project: openProject,
                  ...(openFinishedRunAt !== undefined ? { finishedRunAt: openFinishedRunAt } : {}),
                }
              : e,
          )
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, savedAt, sessionId, openProject, openFinishedRunAt],
  )
  const rows = useMemo(() => searchRecent(recents, q), [recents, q])
  useEffect(() => setActive(0), [q])
  useEffect(() => {
    if (!open) return
    listRef.current
      ?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' })
  }, [active, open, rows])

  if (!open) return null

  const pick = (i: number) => {
    const e = rows[i]
    if (!e) return
    openFromIndex(e.id, () => setOpen(false))
  }

  return (
    <ExplorationSearchBody
      q={q}
      rows={rows}
      active={active}
      openId={sessionId}
      onChange={setQ}
      onActive={setActive}
      onPick={pick}
      onClose={() => setOpen(false)}
      openedAt={openedAt}
      liveMarks={liveMarks}
      inputRef={inputRef}
      listRef={listRef}
      dialogRef={dialogRef}
    />
  )
}
